import { Button } from "@hcsc-fiber/react";
import axios from "axios";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { passKeyAddorDeleteFail, passKeyFetchFail } from "../../constants/static";
import { devicesUrl } from "../../constants/urls";
import { StateContext } from "../../context/stateContext";
import { translations } from "../../translate";
import BiometricFormComponent from "../TrustedDevices/BiometricFormComponent";
import { PasskeyNameDialog, PasskeyNameDialogImperativeHandle } from "../TrustedDevices/PasskeyNameDialog";
import {
  buildPublicKey,
  createInputPlaceholderForBiometric,
  setBiometricPublicKeyValue,
} from "../TrustedDevices/PassKeyUtils";
import { DevicesStepResponse, PassKey } from "../TrustedDevices/TrustedDevice.type";
import styles from "./PassKey.module.scss";

export const PassKeyAdd = ({ closeAlert }: { closeAlert: any }) => {
  const [passKeyStep1Response, setPassKeyStep1Response] = useState<DevicesStepResponse>();
  const [passKeyStepAddResponse, setPassKeyStepAddResponse] = useState<DevicesStepResponse>();
  const [passKeyWebAuthResponse, setPassKeyWebAuthResponse] = useState<DevicesStepResponse>();
  const [passKeyAddResponse, setPassKeyAddResponse] = useState<DevicesStepResponse>();
  const [passKey, setPassKey] = useState<PassKey[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [passKeyName, setPassKeyName] = useState<string>("");
  const {
    cidmUrl,
    accessToken,
    setAddPassKeySuccess,
    setAddedPassKeyMessage,
    languageCode,
    setIsError,
    setPassKeyCount,
    passKeyStep2Response,
    setPassKeyStep2Response,
  } = useContext(StateContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const deviceBackendUrl = cidmUrl + devicesUrl;
  const addNewPasskey = 0;
  const listPasskey = 2;
  const addPasskeyNameOption = "0";
  const skipPasskeyNameOption = "1";
  const defaultPasskeyName = "New Security Key";

  const fetchData = (
    url: string,
    data: any,
    config: any,
    setResponse: React.Dispatch<React.SetStateAction<DevicesStepResponse | undefined>>,
    errorType: string
  ) => {
    axios
      .post<DevicesStepResponse>(url, data, config)
      .then(data => {
        setResponse(data.data);
      })
      .catch(error => {
        setIsError(errorType);
        setIsLoading(false);
      });
  };

  const configPassKey = useMemo(() => {
    return {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
        "accept-api-version": "protocol=1.0,resource=2.1",
        "Content-Type": "application/json",
      },
      withCredentials: true,
      params: {
        authIndexType: "composite_advice",
        authIndexValue:
          "<Advices><AttributeValuePair><Attribute name=\"AuthenticateToServiceConditionAdvice\"/><Value>/members:HCSC_Manage_WebAuthN_Device</Value></AttributeValuePair></Advices>",
      },
    };
  }, [accessToken]);

  //initial cidm call to fetch list of devices on pageload - Step 1
  useEffect(() => {
    if (accessToken && cidmUrl) {
      setIsLoading(true);
      fetchData(deviceBackendUrl, "", configPassKey, setPassKeyStep1Response, passKeyFetchFail);
    }
  }, [cidmUrl, accessToken]);

  const handleCloseDialog = () => {
    // window.location.reload();
    // getList();
  };

  const getList = () => {
    fetchData(deviceBackendUrl, "", configPassKey, setPassKeyStep1Response, passKeyFetchFail);
  };

  useEffect(() => {
    if (passKeyStepAddResponse) {
      const callback = passKeyStepAddResponse?.callbacks;
      if (callback) {
        callback[0].input[0].value = addNewPasskey;
        fetchData(
          deviceBackendUrl,
          { ...passKeyStepAddResponse, callbacks: callback },
          configPassKey,
          setPassKeyAddResponse,
          passKeyAddorDeleteFail
        );
      }
    }
  }, [passKeyStepAddResponse]);

  useEffect(() => {
    if (!passKeyStep1Response || passKeyStep1Response?.callbacks == undefined) return;
    const callback = passKeyStep1Response?.callbacks;
    if (callback) {
      callback[0].input[0].value = listPasskey;
    }
    fetchData(
      deviceBackendUrl,
      { ...passKeyStep1Response, callbacks: callback },
      configPassKey,
      setPassKeyStep2Response,
      passKeyFetchFail
    );
    // }
  }, [passKeyStep1Response]);

  useEffect(() => {
    if (!passKeyStep2Response) return;
    //saving fetched devices in state
    if (passKeyStep2Response) {
      const passKeyArray = passKeyStep2Response.callbacks[0]?.output[1]?.value;
      if (Array.isArray(passKeyArray) && passKeyArray[0] !== " ") {
        const parsedArray: PassKey[] = passKeyArray.map(choice => {
          const [key, value] = choice.split(" : ");
          return { key: key?.trim(), value: value?.trim() };
        });
        setPassKey(parsedArray);
        setPassKeyCount(parsedArray.length);
      } else if (Array.isArray(passKeyArray) && passKeyArray[0] == " ") {
        setPassKey([]);
      } else {
        setIsError(passKeyFetchFail);
      }
      setIsLoading(false);
    }
  }, [passKeyStep2Response]);

  useEffect(() => {
    if (passKeyAddResponse) {
      createInputPlaceholderForBiometric(passKeyAddResponse);
      buildPublicKey(passKeyAddResponse);
    }
  }, [passKeyAddResponse]);

  useEffect(() => {
    if (passKeyWebAuthResponse?.callbacks[0].output[0].value === "Success! Do you want to continue?") {
      setAddPassKeySuccess(true);
      setAddedPassKeyMessage(passKeyName ? passKeyName : defaultPasskeyName);
      setIsDialogOpen(false);
      fetchData(deviceBackendUrl, "", configPassKey, setPassKeyStep1Response, passKeyFetchFail);
    }
  }, [passKeyWebAuthResponse]);

  useEffect(() => {
    if (passKeyName && passKeyWebAuthResponse) {
      const callback = passKeyWebAuthResponse?.callbacks;
      if (callback) {
        callback[0].input[0].value = passKeyName;
        callback[1].input[0].value = addPasskeyNameOption;

        fetchData(
          deviceBackendUrl,
          { ...passKeyWebAuthResponse, callbacks: callback },
          configPassKey,
          setPassKeyAddResponse,
          passKeyAddorDeleteFail
        );
      }
    }
  }, [passKeyName]);

  const addPassKey = () => {
    setPassKeyWebAuthResponse(undefined);
    openDialogFromParent();
    closeAlert();
    setAddPassKeySuccess(false);
    setAddedPassKeyMessage("");
    setPassKeyName("");
    fetchData(deviceBackendUrl, "", configPassKey, setPassKeyStepAddResponse, passKeyAddorDeleteFail);
  };

  const onSubmitHandler = (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    formEvent.stopPropagation();
    const webAuthnOutcomeElement = document.getElementById("webAuthnOutcome") as HTMLInputElement;
    const webAuthnOutcomeValue = webAuthnOutcomeElement ? webAuthnOutcomeElement.value : null;

    if (webAuthnOutcomeValue) {
      const updatedRequestBody = setBiometricPublicKeyValue(passKeyAddResponse);
      fetchData(deviceBackendUrl, updatedRequestBody, configPassKey, setPassKeyWebAuthResponse, passKeyAddorDeleteFail);
    }
  };
  const handleName = (name: string) => {
    setPassKeyName(name);
  };
  const dialogRef = useRef<PasskeyNameDialogImperativeHandle>(null);

  const openDialogFromParent = () => {
    console.log("check ref", dialogRef);
    if (dialogRef.current) {
      dialogRef.current.openDialog(); // Open the dialog from the parent
    }
  };

  const skipAddPassKeyName = () => {
    if (passKeyWebAuthResponse) {
      const callback = passKeyWebAuthResponse?.callbacks;
      if (callback) {
        callback[1].input[0].value = skipPasskeyNameOption;

        fetchData(
          deviceBackendUrl,
          { ...passKeyWebAuthResponse, callbacks: callback },
          configPassKey,
          setPassKeyAddResponse,
          passKeyAddorDeleteFail
        );
      }
    }
  };

  return (
    <>
      {passKeyWebAuthResponse?.callbacks[0].type === "NameCallback" &&
        passKeyWebAuthResponse?.callbacks[0].output[0].value === "Security key name" && (
          <PasskeyNameDialog
            ref={dialogRef}
            handleName={handleName}
            skipAddPassKeyName={skipAddPassKeyName}
            closeDialog={handleCloseDialog}
          />
        )}
      <Button kind="primary" className={styles.button} onClick={() => addPassKey()}>
        {translations.addPasskeyButton[languageCode]}
      </Button>
      <BiometricFormComponent onSubmitHandler={onSubmitHandler} />
    </>
  );
};
